import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'

import { OrderResourcePlanningPanel } from '@/components/orders/order-resource-planning-panel'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { OrderItemMedia } from '@/components/orders/order-item-media'
import { Separator } from '@/components/ui/separator'
import { ordersApi } from '@/services/orders'
import storeCommerceApi from '@/services/store-commerce'
import { getErrorMessage } from '@/lib/api'
import { getPlannerLinkResourceLabel } from '@/lib/planner-links'
import { resolvePackageFlag } from '@/lib/service-package-list-display'
import { formatDate } from '@/lib/utils'

interface OrderDetailsDialogProps {
  referenceId?: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Restrict to packages/particulars only (job management views). */
  viewMode?: 'full' | 'job-management'
}

const formatAmount = (amount?: string | number | null, currency?: string | null, sign?: string | null) => {
  if (amount === null || amount === undefined || amount === '') {
    return '-'
  }

  return `${sign ?? ''}${currency ?? ''}${amount}`
}

const formatMaybeDate = (value?: string | null) => {
  if (!value) {
    return '-'
  }

  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? value : formatDate(parsed)
}

const isMediaEditableOrderStatus = (status?: string | number | null, label?: string | null) => {
  const numeric = Number(status)
  if (Number.isFinite(numeric)) {
    return numeric < 2
  }

  const normalized = String(label ?? status ?? '').trim().toLowerCase()
  if (!normalized) {
    return true
  }

  return !(normalized.includes('placed') || normalized.includes('approved') || normalized.includes('preparing') || normalized.includes('shipping') || normalized.includes('completed'))
}

const toOrderItemId = (value: unknown): string | number | null => {
  if (typeof value === 'string' || typeof value === 'number') {
    return value
  }
  return null
}

const toItemNotes = (item: Record<string, unknown>) => {
  const notes = item.notes
  return typeof notes === 'string' && notes.trim() ? notes.trim() : null
}

export function OrderDetailsDialog({
  referenceId,
  open,
  onOpenChange,
  viewMode = 'full',
}: OrderDetailsDialogProps) {
  const isJobManagementView = viewMode === 'job-management'
  const orderQuery = useQuery({
    queryKey: ['order-detail', referenceId],
    queryFn: () => ordersApi.getOrderDetail(referenceId ?? ''),
    enabled: open && Boolean(referenceId),
  })

  const order = orderQuery.data
  const serveTypesForDetailQuery = useQuery({
    queryKey: ['serve-types-for-order-dialog', order?.service_instance_id ?? 'default'],
    queryFn: () => storeCommerceApi.getServeTypes({
      simple: true,
      service_instance_id: order?.service_instance_id || undefined,
      active: false,
    }),
    enabled: Boolean(order),
    staleTime: 60_000,
  })
  const orderAddressRequired = useMemo(() => {
    if (!order) {
      return null
    }

    const serveTypes = serveTypesForDetailQuery.data ?? []
    const serveTypeId = order.serve_type
    if (serveTypeId != null && String(serveTypeId).trim() !== '') {
      const byId = serveTypes.find((entry) => String(entry.id) === String(serveTypeId))
      if (byId) {
        return Boolean(byId.address_required)
      }
    }

    const normalizedKey = String(order.serve_type_key ?? order.type_label ?? '')
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
    if (!normalizedKey) {
      return null
    }

    const byKey = serveTypes.find((entry) => String(entry.key).trim().toUpperCase() === normalizedKey)
    return byKey ? Boolean(byKey.address_required) : null
  }, [order, serveTypesForDetailQuery.data])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {referenceId
              ? (isJobManagementView ? `Booking packages • #${referenceId}` : `Booking #${referenceId}`)
              : (isJobManagementView ? 'Booking packages' : 'Booking details')}
          </DialogTitle>
          <DialogDescription>
            {isJobManagementView
              ? 'Package and service particulars for this job. Booking fees and payment details are hidden.'
              : 'Full booking detail from the service portal.'}
          </DialogDescription>
        </DialogHeader>

        {orderQuery.isLoading ? (
          <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading booking details...
          </div>
        ) : orderQuery.isError ? (
          <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4 text-sm text-destructive">
            {getErrorMessage(orderQuery.error)}
          </div>
        ) : order ? (
          <div className="space-y-6">
            {!isJobManagementView ? (
              <div className="flex flex-wrap items-center gap-2">
                {order.status_label ? <Badge variant="outline">{order.status_label}</Badge> : null}
                {order.type_label ? <Badge variant="secondary">{order.type_label}</Badge> : null}
                {typeof order.is_paid === 'boolean' ? (
                  <Badge variant={order.is_paid ? 'default' : 'outline'}>{order.is_paid ? 'Paid' : 'Unpaid'}</Badge>
                ) : null}
              </div>
            ) : null}

            {!isJobManagementView ? (
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-3 rounded-xl border border-border/70 bg-muted/20 p-4">
                  <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Booking</p>
                  <div className="space-y-2 text-sm">
                    <div><span className="text-muted-foreground">Booking time:</span> {formatMaybeDate(order.order_time)}</div>
                    <div><span className="text-muted-foreground">Delivery time:</span> {formatMaybeDate(order.delivery_time)}</div>
                    <div><span className="text-muted-foreground">Service branch:</span> {order.service_instance_name || order.service_instance_id || '-'}</div>
                    <div><span className="text-muted-foreground">Payment mode:</span> {order.payment_mode_name || order.payment_method || '-'}</div>
                  </div>
                </div>

                <div className="space-y-3 rounded-xl border border-border/70 bg-muted/20 p-4">
                  <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Customer</p>
                  <div className="space-y-2 text-sm">
                    <div><span className="text-muted-foreground">Name:</span> {order.full_name || order.customer_name || '-'}</div>
                    <div><span className="text-muted-foreground">Email:</span> {order.email || order.customer_email || '-'}</div>
                    <div><span className="text-muted-foreground">Phone:</span> {order.mobile || order.customer_phone || '-'}</div>
                    <div><span className="text-muted-foreground">Address required for type:</span> {orderAddressRequired == null ? '-' : (orderAddressRequired ? 'Yes' : 'No')}</div>
                    <div><span className="text-muted-foreground">Address:</span> {order.address_label || '-'}</div>
                  </div>
                </div>
              </div>
            ) : null}

            {order.order_items.length > 0 ? (
              isJobManagementView ? (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">Packages</h3>
                  <div className="space-y-3">
                    {order.order_items.map((item, index) => {
                      const itemRecord = item as Record<string, unknown>
                      const orderItemId =
                        toOrderItemId(item.id) ??
                        toOrderItemId(itemRecord.order_item) ??
                        toOrderItemId(itemRecord.order_item_id) ??
                        toOrderItemId(item._id) ??
                        toOrderItemId(itemRecord._order_item) ??
                        null
                      const includedFeatures = (item.included_features ?? [])
                        .map((feature) => String(feature?.name || '').trim())
                        .filter(Boolean)
                      const excludedFeatures = (item.excluded_features ?? [])
                        .map((feature) => String(feature?.name || '').trim())
                        .filter(Boolean)
                      const itemNotes = toItemNotes(itemRecord)
                      const canEditMedia = isMediaEditableOrderStatus(order.status, order.status_label)
                      const priorityBookingSelected = resolvePackageFlag(item.priority_booking_selected)

                      return (
                        <div key={`${item.product_name ?? 'item'}-${index}`} className="rounded-xl border border-border/70 p-4">
                          <div className="space-y-1">
                            <p className="font-medium text-foreground">{item.product_name || 'Unnamed package'}</p>
                            <p className="text-sm text-muted-foreground">
                              {[item.product_variant_name, ...(item.addon_names ?? [])].filter(Boolean).join(' • ') || '-'}
                            </p>
                            {item.qty != null ? (
                              <p className="text-sm text-muted-foreground">Qty {item.qty}</p>
                            ) : null}
                            {priorityBookingSelected ? (
                              <div className="flex flex-wrap gap-1 pt-1">
                                <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
                                  Priority booking • 45 min service{Number(item.priority_fee) > 0 ? ` +${formatAmount(item.priority_fee)}` : ''}
                                </Badge>
                              </div>
                            ) : null}
                          </div>

                          {item.planner_resource_links && item.planner_resource_links.length > 0 ? (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {item.planner_resource_links.map((link) => (
                                <Badge key={String(link.id)} variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">
                                  {getPlannerLinkResourceLabel(link)} x{link.quantity ?? 1}
                                </Badge>
                              ))}
                            </div>
                          ) : null}

                          <div className="mt-3 space-y-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-xs font-medium text-emerald-700">Included</span>
                              {includedFeatures.length > 0 ? (
                                includedFeatures.map((name, featureIndex) => (
                                  <Badge key={`inc-${featureIndex}-${name}`} variant="outline" className="border-emerald-300 text-emerald-700">
                                    {name}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-xs text-muted-foreground">None</span>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-xs font-medium text-rose-700">Excluded</span>
                              {excludedFeatures.length > 0 ? (
                                excludedFeatures.map((name, featureIndex) => (
                                  <Badge key={`exc-${featureIndex}-${name}`} variant="outline" className="border-rose-300 text-rose-700">
                                    {name}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-xs text-muted-foreground">None</span>
                              )}
                            </div>
                          </div>

                          {itemNotes ? (
                            <p className="mt-3 text-sm text-muted-foreground">{itemNotes}</p>
                          ) : null}

                          <OrderItemMedia
                            orderItemId={orderItemId}
                            serviceInstanceId={order.service_instance_id}
                            canEdit={canEditMedia}
                          />
                        </div>
                      )
                    })}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">Items</h3>
                  <div className="overflow-hidden rounded-xl border border-border/70">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/35 text-left text-xs uppercase tracking-[0.16em] text-muted-foreground">
                        <tr>
                          <th className="px-4 py-3">Item</th>
                          <th className="px-4 py-3">Qty</th>
                          <th className="px-4 py-3">Unit</th>
                          <th className="px-4 py-3">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.order_items.map((item, index) => {
                          const itemRecord = item as Record<string, unknown>
                          const orderItemId =
                            toOrderItemId(item.id) ??
                            toOrderItemId(itemRecord.order_item) ??
                            toOrderItemId(itemRecord.order_item_id) ??
                            toOrderItemId(item._id) ??
                            toOrderItemId(itemRecord._order_item) ??
                            null
                          const canEditMedia = isMediaEditableOrderStatus(order.status, order.status_label)
                          const priorityBookingSelected = resolvePackageFlag(item.priority_booking_selected)
                          return (
                            <tr key={`${item.product_name ?? 'item'}-${index}`} className="border-t border-border/60">
                              <td className="px-4 py-3">
                                <div>
                                  <p className="font-medium text-foreground">{item.product_name || 'Unnamed item'}</p>
                                  <p className="text-muted-foreground">
                                    {[item.product_variant_name, ...(item.addon_names ?? [])].filter(Boolean).join(' • ') || '-'}
                                  </p>
                                  {item.planner_resource_links && item.planner_resource_links.length > 0 ? (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                      {item.planner_resource_links.map((link) => (
                                        <Badge key={String(link.id)} variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">
                                          {getPlannerLinkResourceLabel(link)} x{link.quantity ?? 1}
                                        </Badge>
                                      ))}
                                    </div>
                                  ) : null}
                                  {item.offer_title || item.discount_value ? (
                                    <div className="mt-1">
                                      <Badge variant="secondary" className="bg-rose-50 text-rose-700 border-rose-200">
                                        {item.offer_title || 'Offer'}
                                        {item.discount_value ? ` -${item.discount_value}${item.discount_type === '1' ? '%' : ''}` : ''}
                                      </Badge>
                                    </div>
                                  ) : null}
                                  {priorityBookingSelected ? (
                                    <div className="mt-1">
                                      <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
                                        Priority booking • 45 min service{Number(item.priority_fee) > 0 ? ` +${formatAmount(item.priority_fee)}` : ''}
                                      </Badge>
                                    </div>
                                  ) : null}
                                  <OrderItemMedia
                                    orderItemId={orderItemId}
                                    serviceInstanceId={order.service_instance_id}
                                    canEdit={canEditMedia}
                                  />
                                </div>
                              </td>
                              <td className="px-4 py-3">{item.qty ?? '-'}</td>
                              <td className="px-4 py-3">{item.unit_price ?? '-'}</td>
                              <td className="px-4 py-3">{item.final_price ?? '-'}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            ) : (
              isJobManagementView ? (
                <p className="text-sm text-muted-foreground">No packages on this booking.</p>
              ) : null
            )}

            {!isJobManagementView && order.totals.length > 0 ? (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">Totals</h3>
                <div className="rounded-xl border border-border/70 bg-muted/20 p-4">
                  <div className="space-y-2 text-sm">
                    {order.totals.map((line) => (
                      <div key={line.label} className="flex items-center justify-between gap-4">
                        <span className="text-muted-foreground">{line.label}</span>
                        <span className="font-medium text-foreground">{formatAmount(line.amount, line.currency, line.sign)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}

            {order.notes ? (
              <>
                {!isJobManagementView ? <Separator /> : null}
                <div className="space-y-2 text-sm">
                  <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Notes</p>
                  <p>{order.notes}</p>
                </div>
              </>
            ) : null}

            {!isJobManagementView ? (
              <>
                <Separator />

                <OrderResourcePlanningPanel
                  order={order}
                  referenceId={referenceId}
                  serviceInstanceId={order.service_instance}
                  showLinkManager={false}
                  onChanged={async () => {
                    await orderQuery.refetch()
                  }}
                />
              </>
            ) : null}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

export default OrderDetailsDialog